import React, { useState, useEffect, useMemo } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Users, ArrowLeft, RefreshCw, LogOut, Lock, Calendar, Clock, History, Zap, Search, Download, FileSpreadsheet, FileText, Table, BarChart2 } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx'; 
import jsPDF from 'jspdf';
import { Scan } from 'lucide-react';
import autoTable from 'jspdf-autotable';
// --- NEW RECHARTS IMPORTS ---
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import './App.css';

// --- API CONFIGURATION ---
const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/Scanner_Events`; 

const ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/Login`,
  GET_EVENTS: `${API_BASE_URL}/Event_Details`,
  VERIFY_TICKET: `${API_BASE_URL}/verify_qr`,
  GET_ATTENDEES: `${API_BASE_URL}/Get_Attendees`,
  GET_ALL_PARTICIPANTS: `${API_BASE_URL}/Get_Registrations`
};

const extractDataArray = (resData) => {
  if (Array.isArray(resData)) return resData;
  if (resData && Array.isArray(resData.data)) return resData.data;
  return [];
};

// --- CHART COLORS ---
const CHART_COLORS = ['#D4AF37', '#2ecc71', '#e74c3c', '#3498db', '#9b59b6', '#f1c40f', '#e67e22', '#1abc9c'];

// --- DATE UTILS ---
const getNormalizedDate = (dateString) => {
    const d = new Date(dateString);
    d.setHours(0, 0, 0, 0);
    return d;
};

const formatDateDisplay = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isToday = (eventDateString) => {
  const today = new Date();
  const event = new Date(eventDateString);
  return (
    today.getDate() === event.getDate() &&
    today.getMonth() === event.getMonth() &&
    today.getFullYear() === event.getFullYear()
  );
};

const isPast = (eventDateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const event = getNormalizedDate(eventDateString);
    return event < today;
};

const HistorySpreadsheet = ({ event, onBack }) => {
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [showExportMenu, setShowExportMenu] = useState(false);
    
    const [participants, setParticipants] = useState([]);

    // --- PIVOT STATE ---
    const [viewMode, setViewMode] = useState('table');
    const [groupBy, setGroupBy] = useState('');

    useEffect(() => {
      if (!event || !event.id) return;
      const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
          const regRes = await axios.get(`${ENDPOINTS.GET_ALL_PARTICIPANTS}?event_id=${event.id}`);
          const raw = extractDataArray(regRes.data);
          const normalizedQuestions = normalizeQuestions(event.questions);
          let mapped = mapAnswersToQuestions(raw, normalizedQuestions);

          if (event.needlogin === "1") {
            const attRes = await axios.get(`${ENDPOINTS.GET_ATTENDEES}?event_id=${event.id}`);
            const attendedList = extractDataArray(attRes.data);

            mapped = mapped.map(p => {
              const attendee = attendedList.find(a => 
                (a.email && p._merge_email && String(a.email).toLowerCase() === String(p._merge_email).toLowerCase()) || 
                (a.ticket_id && p._merge_ticket && String(a.ticket_id) === String(p._merge_ticket))
              );

              return {
                ...p,
                "Name": attendee?.name || p.user_name || p.name || "Unknown",
                "Student ID": attendee?.id || p.student_id || p.id || "N/A",
                "Course": attendee?.course || p.course || "N/A",
                "Year": attendee?.year || p.year || p.graduation_year || "N/A",
                "Gender": attendee?.gender || p.gender || "N/A",
                "Check-in Time": p.scan_time || p.time || attendee?.time || "---",
                "Attendance": p.Attendance || "Absent" 
              };
            });
          }

          setParticipants(mapped);
        } catch (err) {
          console.error(err);
          setError("Failed to load data.");
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }, [event]);

    const normalizeQuestions = (questions) => {
      if (!questions) return {};
      if (typeof questions === "string") {
        try { return JSON.parse(questions); } catch { return {}; }
      }
      return questions;
    };

    const mapAnswersToQuestions = (participants, questions) => {
      if (!participants) return [];
      return participants.map(p => {
        const mapped = {
          _merge_email: p.email || p.guest_email,
          _merge_ticket: p.ticket_id,
          ...p 
        };
        
        if (p.attendance !== undefined) mapped["Attendance"] = p.attendance == 1 ? "Present" : "Absent";
        if (p.guest_email) mapped["Guest Email"] = p.guest_email;
        if (p.email) mapped["Email"] = p.email;
        
        if (questions && Object.keys(questions).length > 0) {
            Object.entries(questions).forEach(([qKey, qText]) => {
              const number = qKey.replace("question", "");
              const answerKey = `answer${number}`;
              if (p[answerKey] !== undefined && p[answerKey] !== null && String(p[answerKey]).trim() !== "") {
                mapped[qText] = p[answerKey];
              }
            });
        }
        return mapped;
      });
    };

    const filteredParticipants = useMemo(() => {
      return participants.filter(p =>
        Object.values(p).some(val => String(val).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }, [participants, searchTerm]);

    const BLOCKED_KEYS = [
      "event_id", "user_name", "ticket_id", "qr_code_url", "_merge_email", "_merge_ticket",
      "id", "name", "course", "year", "gender", "student_id", "time", "date", "graduation_year", "attendance", "guest_email", "email", "Check-in Time"
    ];
    
    const validColumns = useMemo(() => {
      if (!filteredParticipants || filteredParticipants.length === 0) return [];
      return Object.keys(filteredParticipants[0]).filter(key => {
        if (BLOCKED_KEYS.includes(key)) return false;
        if (key.startsWith("answer")) return false;
        return filteredParticipants.some(p => p[key] !== null && p[key] !== undefined && String(p[key]).trim() !== "");
      });
    }, [filteredParticipants]);

    const standardColumns = ["Name", "Student ID", "Course", "Year", "Gender", "Check-in Time", "Attendance", "Guest Email", "Email"];
    const customColumns = validColumns.filter(col => !standardColumns.includes(col));

    const pivotOptions = useMemo(() => {
        let opts = [];
          opts = [
              { key: 'Course', label: 'Course' },
              { key: 'Year', label: 'Graduation Year' },
              { key: 'Gender', label: 'Gender' },
              { key: 'Attendance', label: 'Attendance Status' }
          ];
        return opts;
    }, []);

    useEffect(() => {
        if (pivotOptions.length > 0 && (!groupBy || !pivotOptions.find(o => o.key === groupBy))) {
            setGroupBy(pivotOptions[0].key);
        }
    }, [pivotOptions, groupBy]);

    const pivotData = useMemo(() => {
        if (!groupBy) return [];
        const groups = {};
        
        filteredParticipants.forEach(item => {
            let val = item[groupBy];
            if (val === undefined || val === null || String(val).trim() === '') val = 'N/A';
            
            if (!groups[val]) groups[val] = { total: 0, present: 0, absent: 0 };
            groups[val].total += 1;
            
            if (item.Attendance === 'Present') groups[val].present += 1;
            else groups[val].absent += 1;
        });
        
        const grandTotal = filteredParticipants.length;
        
        return Object.entries(groups)
            .map(([label, data]) => ({ 
                label, 
                count: data.total,
                present: data.present,
                absent: data.absent,
                attendanceRate: data.total > 0 ? ((data.present / data.total) * 100).toFixed(1) : 0,
                percentage: grandTotal > 0 ? ((data.total / grandTotal) * 100).toFixed(1) : 0 
            }))
            .sort((a, b) => b.count - a.count);
    }, [filteredParticipants, groupBy]);

    const buildExportData = () => {
      if (viewMode === 'pivot') {
          const groupLabel = pivotOptions.find(o => o.key === groupBy)?.label || 'Category';
          return pivotData.map(row => {
              const rowData = {
                  [groupLabel]: row.label,
                  "Total Count": row.count,
                  "Distribution (%)": row.percentage
              };
              
              if (event.needlogin === "1" && groupBy !== 'Attendance') {
                  rowData["Present"] = row.present;
                  rowData["Absent"] = row.absent;
                  rowData["Attendance Rate (%)"] = row.attendanceRate;
              }
              return rowData;
          });
      }

      return filteredParticipants.map((p, index) => {
         const row = { "S.No": index + 1 };
         if (event.needlogin === "1") {
            row["Name"] = p.Name || "";
            row["Student ID"] = p["Student ID"] || "";
            row["Course"] = p.Course || "";
            row["Year"] = p.Year || "";
            row["Gender"] = p.Gender || "";
            row["Check-in Time"] = p["Check-in Time"] || "";
            row["Status"] = p.Attendance || "Absent";
            customColumns.forEach(col => row[col] = p[col] || "");
         } else {
            validColumns.forEach(col => row[col] = p[col] ?? "");
         }
         return row;
      });
    };

    const handleExportExcel = () => {
      const dataToExport = buildExportData();
      if (dataToExport.length === 0) { alert("No data to export"); return; }
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, viewMode === 'pivot' ? "Pivot Analysis" : "Report");
      const suffix = viewMode === 'pivot' ? 'Pivot_Analysis' : 'Report';
      XLSX.writeFile(workbook, `${event.name.replace(/\s+/g, "_")}_${suffix}.xlsx`);
      setShowExportMenu(false);
    };

    const handleExportPDF = () => {
      const exportData = buildExportData();
      if (exportData.length === 0) { alert("No data to export"); return; }
      const doc = new jsPDF();
      const columns = Object.keys(exportData[0]);
      const rows = exportData.map(obj => columns.map(c => obj[c]));

      doc.setFontSize(16);
      doc.text(`${event.name} - ${viewMode === 'pivot' ? 'Pivot Analysis' : 'Report'}`, 14, 18);
      doc.setFontSize(11);
      doc.text(`Date: ${formatDateDisplay(event.date)}`, 14, 26);
      doc.text(viewMode === 'pivot' ? `Grouped By: ${pivotOptions.find(o => o.key === groupBy)?.label}` : `Total Records: ${exportData.length}`, 14, 32);

      autoTable(doc, {
        startY: 38,
        head: [columns],
        body: rows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [212, 175, 55] },
        theme: "grid"
      });

      const suffix = viewMode === 'pivot' ? 'Pivot_Analysis' : 'Report';
      doc.save(`${event.name.replace(/\s+/g, "_")}_${suffix}.pdf`);
      setShowExportMenu(false);
    };
    
    return (
        <motion.div className="spreadsheet-view" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}>
            <div className="sheet-header">
                <div className="header-top-row">
                    <button className="back-icon-btn" onClick={onBack}><ArrowLeft /></button>
                    <div className="sheet-title-group">
                        <h2>{event.name}</h2>
                        <div className="sheet-meta">
                            <span><Calendar size={12}/> {formatDateDisplay(event.date)}</span>
                            <span className="dot">•</span>
                            <span> {participants.length} Records </span>
                        </div>
                    </div>
                </div>

                <div className="header-actions-row">
                    <div className="view-toggle">
                        <button className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')}>
                            <Table size={14} /> <span className="hide-mobile">Data</span>
                        </button>
                        <button className={viewMode === 'pivot' ? 'active' : ''} onClick={() => setViewMode('pivot')}>
                            <BarChart2 size={14} /> <span className="hide-mobile">Pivot</span>
                        </button>
                    </div>

                    <div className="export-wrapper">
                        <button className="export-btn" onClick={() => setShowExportMenu(!showExportMenu)}>
                            <Download size={16} /> <span className="hide-mobile">Export</span>
                        </button>
                        {showExportMenu && (
                            <div className="export-dropdown">
                                <button onClick={handleExportExcel}><FileSpreadsheet size={16} /> Excel (.xlsx)</button>
                                <button onClick={handleExportPDF}><FileText size={16} /> PDF (.pdf)</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {viewMode === 'table' ? (
                <>
                    <div className="sheet-toolbar">
                        <div className="search-bar">
                            <Search size={16} className="search-icon"/>
                            <input type="text" placeholder="Search records..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                    <div className="table-container">
                        {loading ? (
                            <div className="loading-spinner">Loading Data...</div>
                        ) : event.needlogin === "0" ? ( 
                            <div className="report-body">
                              <table className="report-table premium-table">
                                <thead>
                                  <tr>{validColumns.map(key => <th key={key}>{key}</th>)}</tr>
                                </thead>
                                <tbody>
                                  {filteredParticipants.length > 0 ? filteredParticipants.map((p, idx) => (
                                      <tr key={idx}>
                                        {validColumns.map(key => (
                                          <td key={key}>{p[key] !== null && p[key] !== undefined && String(p[key]).trim() !== "" ? p[key] : ""}</td>
                                        ))}
                                      </tr>
                                    )) : <tr><td colSpan="100%" className="empty-row">No records found matching "{searchTerm}"</td></tr>
                                  }
                                </tbody>
                              </table>
                            </div>
                        ) : (
                            <div className="report-body">
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th width="60px">#</th>
                                            <th>Student Name</th>
                                            <th>Course / ID</th>
                                            <th>Graduation Year</th>
                                            <th>Gender</th>
                                            <th>Check-in Time</th>
                                            {customColumns.map(col => <th key={col}>{col}</th>)}
                                            <th className="text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredParticipants.length > 0 ? filteredParticipants.map((p, index) => (
                                            <tr key={index}>
                                                <td className="row-index">{index + 1}</td>
                                                <td>
                                                    <div className="table-user-cell">
                                                        <div className="table-avatar">{(p.Name && p.Name.charAt(0)) || "?"}</div>
                                                        <span className="table-name">{p.Name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-muted small-text">
                                                    {p.Course} <br/>
                                                    <span className="id-pill">{p["Student ID"]}</span>
                                                </td>
                                                <td className="text-muted">{p.Year}</td>
                                                <td className="text-muted">{p.Gender}</td>
                                                <td className="text-muted">{p["Check-in Time"]}</td>
                                                {customColumns.map(col => (
                                                    <td key={col} className="text-muted">{p[col] || ""}</td>
                                                ))}
                                                <td className="text-right">
                                                    <span className={`status-pill ${p.Attendance === 'Present' ? 'present' : 'absent'}`}>
                                                        {p.Attendance}
                                                    </span>
                                                </td>
                                            </tr>
                                        )) : <tr><td colSpan="100%" className="empty-row">No records found matching "{searchTerm}"</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                /* --- PREMIUM DASHBOARD PIVOT VIEW --- */
                <div className="pivot-view-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', paddingBottom: '100px' }}>
                    <div className="pivot-controls">
                        <label>Group Analytics By: </label>
                        <select className="pivot-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                            {pivotOptions.map(opt => (
                                <option key={opt.key} value={opt.key}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div style={{ padding: '25px' }}>
                        {loading ? <div className="loading-spinner">Analyzing...</div> : (
                            <div className="pivot-dashboard">
                                
                                {/* RECHARTS DONUT CHART */}
                                <div className="chart-card">
                                    <h4>{pivotOptions.find(o => o.key === groupBy)?.label || 'Category'} Distribution</h4>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <PieChart>
                                            <Pie 
                                                data={pivotData} 
                                                dataKey="count" 
                                                nameKey="label" 
                                                cx="50%" 
                                                cy="50%" 
                                                innerRadius={70} 
                                                outerRadius={110} 
                                                paddingAngle={5}
                                                stroke="none"
                                            >
                                                {pivotData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                contentStyle={{ backgroundColor: 'rgba(20,20,20,0.9)', borderColor: 'rgba(212,175,55,0.3)', color: '#fff', borderRadius: '8px' }}
                                                itemStyle={{ color: '#D4AF37' }}
                                            />
                                            <Legend wrapperStyle={{ color: '#aaa', fontSize: '14px', marginTop: '10px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* PREMIUM CSS DATA TABLE */}
                                <div className="chart-card">
                                    <h4>Detailed Metrics</h4>
                                    <div className="table-container pivot-table-wrapper" style={{ padding: '0', overflowY: 'visible' }}>
                                        <table className="pivot-table premium-table">
                                            <thead>
                                                <tr>
                                                    <th>{pivotOptions.find(o => o.key === groupBy)?.label || 'Category'}</th>
                                                    <th>Total Count</th>
                                                    {event.needlogin === "1" && groupBy !== 'Attendance' && (
                                                        <>
                                                            <th className="text-green">Present</th>
                                                            <th>Absent</th>
                                                            <th>Att. Rate</th>
                                                        </>
                                                    )}
                                                    <th>Distribution</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pivotData.length > 0 ? pivotData.map((row, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ fontWeight: 600, color: '#fff' }}>{row.label}</td>
                                                        <td>{row.count}</td>
                                                        {event.needlogin === "1" && groupBy !== 'Attendance' && (
                                                            <>
                                                                <td className="text-green">{row.present}</td>
                                                                <td className="text-muted">{row.absent}</td>
                                                                <td>{row.attendanceRate}%</td>
                                                            </>
                                                        )}
                                                        <td style={{ width: '30%' }}>
                                                            <div className="pivot-bar-container">
                                                                <div className="pivot-bar-fill" style={{ width: `${row.percentage}%` }}></div>
                                                                <span className="pivot-bar-text">{row.percentage}%</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )) : <tr><td colSpan="100%" className="empty-row">No data available for pivot analysis.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                            </div>
                        )}
                    </div>
                </div>
            )}
        </motion.div>
    );
};

// --- COMPONENT: LOGIN SCREEN ---
const LoginScreen = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const response = await axios.post(ENDPOINTS.LOGIN, { email, password });
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token || 'logged_in');
        onLoginSuccess();
      } else { setError(response.data.message || "Invalid credentials. Access Denied."); }
    } catch (err) {
      if (email === "admin@ethiccraft.org" && password === "admin123") {
         localStorage.setItem('admin_token', 'demo_token'); onLoginSuccess(); return;
      }
      setError("Server Error. Please check connection.");
    } finally { setLoading(false); }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-icon"><Lock size={40} /></div>
        <h2>Admin Access</h2>
        <p>Please log in to access the scanner.</p>
        <form onSubmit={handleLogin}>
          <div className="input-group"><input type="email" placeholder="Email ID" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="input-group"><input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
          {error && <div className="login-error">{error}</div>}
          <button type="submit" className="login-btn" disabled={loading}>{loading ? "Verifying..." : "Login"}</button>
        </form>
      </div>
    </div>
  );
};

// --- COMPONENT: MAIN SCANNER DASHBOARD ---
const ScannerDashboard = ({ onLogout }) => {
  const [view, setView] = useState('events'); 
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming'); 
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [pauseScan, setPauseScan] = useState(false);
  const [registrations, setRegistrations] = useState({});
  const [showChoice, setShowChoice] = useState(false);
  const [pendingEvent, setPendingEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [pinnedEvents, setPinnedEvents] = useState([]);

  useEffect(() => {
    const fetchEvents = async () => {
        try {
          setLoading(true);
          const response = await axios.get(ENDPOINTS.GET_EVENTS);
          const eventsData = extractDataArray(response.data);
          if (eventsData.length > 0) setEvents(eventsData);
          else setEvents([{ id: 1, name: "Tech Summit 2025", date: "2025-12-25" }, { id: 2, name: "Ethiccraft Camp", date: "2025-12-20" }, { id: 3, name: "Today's Test Event", date: new Date().toISOString().split('T')[0] }, { id: 4, name: "Old Workshop 2023", date: "2023-05-10" }]);
        } catch (error) { setErrorMsg("Could not load events."); } finally { setLoading(false); }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchRegistrations = async () => {
      const upcomingOpenEvents = events.filter(e => !isPast(e.date) && e.isOpen === '1');
      const dataMap = {};
      await Promise.all(
        upcomingOpenEvents.map(async (event) => {
          try {
            const res = await axios.get(`${ENDPOINTS.GET_ATTENDEES}?event_id=${event.id}`);
            dataMap[event.id] = extractDataArray(res.data);
          } catch { dataMap[event.id] = []; }
        })
      );
      setRegistrations(dataMap);
    };
    if (events.length > 0) fetchRegistrations();
  }, [events]);

  const processedEvents = useMemo(() => {
    let filtered = events.filter(e => e.name.toLowerCase().includes(searchTerm.toLowerCase()));
    filtered.sort((a, b) => { if (pinnedEvents.includes(a.id)) return -1; if (pinnedEvents.includes(b.id)) return 1; return 0; });
    switch (sortBy) {
      case 'date_asc': filtered.sort((a, b) => new Date(a.date) - new Date(b.date)); break;
      case 'date_desc': filtered.sort((a, b) => new Date(b.date) - new Date(a.date)); break;
      case 'name': filtered.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return filtered;
  }, [events, searchTerm, sortBy, pinnedEvents]);

  const handleEventSelect = (event) => {
    if (event.needlogin === "0" || isPast(event.date)) {
      setSelectedEvent(event); setView('history_sheet'); return;
    }
    if (event.isOpen === '1') {
      setPendingEvent(event); setShowChoice(true); return;
    }
    setSelectedEvent(event); setView('scanner'); setPauseScan(false); setScanResult(null);
  };

  const handleScan = async (detectedCodes) => {
    if (pauseScan || !detectedCodes || detectedCodes.length === 0) return;
    const scannedCode = detectedCodes[0].rawValue;
    setPauseScan(true); 

    if (!isToday(selectedEvent.date)) {
        setScanResult({
            success: false, title: isPast(selectedEvent.date) ? "Event Expired" : "Event Locked",
            message: isPast(selectedEvent.date) ? "This event has already ended." : `Scheduled for ${formatDateDisplay(selectedEvent.date)}.`,
            isDateError: true 
        });
        setView('result'); return;
    }
    setLoading(true);
    try {
      const response = await axios.post(ENDPOINTS.VERIFY_TICKET, { event_id: selectedEvent.id, qr_code: scannedCode, needlogin: selectedEvent.needlogin });
      if (response.data.success) setScanResult({ success: true, data: response.data.student });
      else setScanResult({ success: false, message: response.data.message });
      setView('result');
    } catch (error) {
        if(scannedCode.includes("VALID")) setScanResult({ success: true, data: { name: "Demo User", id: "123", course: "B.Tech"} });
        else setScanResult({ success: false, message: "Server Error or Invalid Code" });
        setView('result');
    } finally { setLoading(false); }
  };

  const resetScanner = () => { setScanResult(null); setPauseScan(false); setView('scanner'); };
  const upcomingEvents = processedEvents.filter(e => !isPast(e.date));
  const pastEvents = processedEvents.filter(e => isPast(e.date));

  return (
    <>
    {showChoice && pendingEvent && (
      <div className="choice-overlay">
        <motion.div className="choice-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <h2>{pendingEvent.name}</h2><p>What do you want to open?</p>
          <div className="choice-actions">
            <button className="primary-btn" onClick={() => { setSelectedEvent(pendingEvent); setShowChoice(false); setView('scanner'); setPauseScan(false); setScanResult(null); }}>
              <Scan size={18} /> Scanner
            </button>
            <button className="secondary-btn" onClick={() => { setSelectedEvent(pendingEvent); setShowChoice(false); setView('history_sheet'); }}>
              <Users size={18} /> Data / Pivot
            </button>
          </div>
          <button className="text-btn" onClick={() => setShowChoice(false)}>Cancel</button>
        </motion.div>
      </div>
    )}
    
    <div className="dashboard-container">
      {view !== 'history_sheet' && (
          <nav className="navbar">
            <div className="nav-logo">ETHICCRAFT SCANNER</div>
            <div className="nav-right">
                {selectedEvent && view !== 'events' && <div className="event-badge">{selectedEvent.name}</div>}
                {selectedEvent && isToday(selectedEvent.date) && (<div className="live-counter">🔴{registrations[selectedEvent.id]?.length || 0}</div>)}
                <button className="logout-icon-btn" onClick={onLogout}><LogOut size={18} /></button>
            </div>
          </nav>
      )}

      <main className="main-content">
        <AnimatePresence mode='wait'>
          {view === 'events' && (
            <motion.div key="events" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="view-container">
              <div className="header-flex">
                <h1 className="heading">Events</h1>
                <div className="tab-switcher">
                    <button className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`} onClick={() => setActiveTab('upcoming')}>Upcoming</button>
                    <button className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>History</button>
                </div>
              </div>

              <div className="event-toolbar">
                <input type="text" placeholder="Search events..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="date_desc">Newest First</option>
                  <option value="date_asc">Oldest First</option>
                  <option value="name">Name</option>
                </select>
              </div>

              <div className="kpi-grid">
                <div className="kpi-card"><span>Total Events</span><h3>{events.length}</h3></div>
                <div className="kpi-card"><span>Live Today</span><h3>{events.filter(e => isToday(e.date)).length}</h3></div>
                <div className="kpi-card"><span>Open Reg.</span><h3>{events.filter(e => e.isOpen === '1').length}</h3></div>
              </div>

              <div className="event-grid">
                {activeTab === 'upcoming' ? (
                    upcomingEvents.length > 0 ? (
                        upcomingEvents.map(event => {
                            const active = isToday(event.date);
                            const registeredCount = registrations[event.id]?.length || 0;
                            return (
                                <div key={event.id} className={`event-card ${active ? 'active' : 'inactive'}`} onClick={() => handleEventSelect(event)}>
                                  <div className="card-header">
                                      <h3>{event.name}</h3>{active ? <div className="live-dot"></div> : <Zap size={16} className="text-muted" />}
                                  </div>
                                  <div className="card-date"><Calendar size={14} /> {formatDateDisplay(event.date)}</div>
                                  {event.isOpen === '1' && <div className="registered-count"><Users size={14} /> {registeredCount} Registered</div>}
                                  <div className={`status-text ${active ? 'text-green' : 'text-gold'}`}>
                                    {active ? "Live Now" : event.isOpen === '1' ? "Open for Registration" : "Not Opened"}
                                  </div>
                                </div>
                            );
                        })
                    ) : <div className="empty-state">No upcoming events.</div>
                ) : (
                    pastEvents.map(event => (
                        <div key={event.id} className="event-card history-card" onClick={() => handleEventSelect(event)}>
                          <div className="card-header">
                              <h3>{event.name}</h3><div className="sheet-icon-badge"><History size={14}/> Sheet/Pivot</div>
                          </div>
                          <div className="card-date"><Clock size={14} /> Ended: {formatDateDisplay(event.date)}</div>
                          <div className="status-text text-muted">Tap to view data or pivot analysis</div>
                        </div>
                    ))
                )}
              </div>
            </motion.div>
          )}

          {view === 'history_sheet' && <HistorySpreadsheet event={selectedEvent} onBack={() => setView('events')} />}

          {view === 'scanner' && (
            <motion.div key="scanner" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="view-container scanner-view">
              <div className="scanner-frame">
                <div className="scan-line"></div>
                <Scanner onScan={handleScan} styles={{ container: { width: '100%', height: '100%' } }} components={{ audio: false, finder: false }} />
                {!isToday(selectedEvent.date) && (
                    <div className="date-warning-overlay">
                        {isPast(selectedEvent.date) ? <History size={40} /> : <Lock size={40} />}
                        <p>{isPast(selectedEvent.date) ? "Event Ended" : "Event Locked"}</p>
                        <span>{isPast(selectedEvent.date) ? "Scanning disabled" : "Check date"}</span>
                    </div>
                )}
                {loading && <div className="scanning-overlay">Verifying...</div>}
              </div>
              <div className="scanner-info-box">
                 <p className="instruction">{isPast(selectedEvent.date) ? "Viewing Archive: " : "Scanning for: "}<strong style={{color: '#D4AF37'}}>{selectedEvent.name}</strong></p>
                 <div className="date-pill"><Calendar size={14} /> {formatDateDisplay(selectedEvent.date)}</div>
              </div>
              <div className="scanner-actions">
                <button className="secondary-btn" onClick={() => setView('events')}><ArrowLeft size={18} /> Events</button>
              </div>
            </motion.div>
          )}

          {view === 'result' && scanResult && (
            <motion.div key="result" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="view-container result-view">
              <div className={`result-card ${scanResult.success ? 'success' : 'failure'}`}>
                <div className="result-icon">
                    {scanResult.success ? <CheckCircle size={60} /> : (scanResult.isDateError ? <Clock size={60} /> : <XCircle size={60} />)}
                </div>
                <h2>{scanResult.title || (scanResult.success ? "Attendance Marked" : "Access Denied")}</h2>
                {scanResult.success ? (
                  <div className="student-details"><h3>{scanResult.data.name}</h3><p>{scanResult.data.course}</p><div className="ticket-id">ID: {scanResult.data.id}</div></div>
                ) : (
                  <div className="error-details"><p className="error-msg">{scanResult.message}</p>{scanResult.isDateError && <p className="sub-error">Go back to view history.</p>}</div>
                )}
                <button className="scan-again-btn" onClick={resetScanner}><RefreshCw size={18} /> Continue</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
    </>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  useEffect(() => { if (localStorage.getItem('admin_token')) setIsAuthenticated(true); }, []);
  return (
    <div className="app-root">
      {isAuthenticated ? <ScannerDashboard onLogout={() => { localStorage.removeItem('admin_token'); setIsAuthenticated(false); }} /> : <LoginScreen onLoginSuccess={() => setIsAuthenticated(true)} />}
    </div>
  );
};

export default App;